import { useTranslation } from 'react-i18next';
import { Popover } from '@/components/Popover';
import { useMaybeControlledDisclosure } from '@/pages/Schedule/hooks';
import colorWheelIcon from '@/pages/Schedule/icons/color-wheel.png';
import { getEntryCategoryColors } from '@/pages/Schedule/utils/entry';
import { Flex } from '@/ui';
import { EntryModalField } from '../../EntryModal/components/EntryModalField';
import { EntryModalFieldButton } from '../../EntryModal/components/EntryModalFieldButton';
import { CategorySelectContextProvider } from '../CategorySelectContextProvider';
import { ScheduleCategoryDataProvider } from '../ScheduleCategoryDataProvider';
import type { Category } from '../types';
import { CategorySelect } from './CategorySelect';

type Props = {
  isOpen?: boolean;
  selected?: Category | null;
  onSelect: (category: Category | null) => void;
  onOpen?: () => void;
  onClose?: () => void;
};

export const CategorySheet = ({
  isOpen: controlledIsOpen,
  onOpen: controlledOnOpen,
  onClose: controlledOnClose,
  selected,
  onSelect,
}: Props) => {
  const { isOpen, onOpen, onClose } = useMaybeControlledDisclosure({
    isOpen: controlledIsOpen,
    onOpen: controlledOnOpen,
    onClose: controlledOnClose,
  });

  const colors = getEntryCategoryColors(selected?.color);
  const { t } = useTranslation('entryModal');

  const trigger = (
    <EntryModalField
      icon={<img alt="" height="18px" src={colorWheelIcon} width="18px" />}
    >
      <EntryModalFieldButton
        _hover={selected ? { bg: 'transparent' } : undefined}
        alignItems="start"
        display="flex"
        flex={selected ? 'none' : 1}
        flexDir="column"
        justifyContent="center"
        paddingBottom="0"
        paddingTop="0"
        placeholder={selected ? undefined : t('category.placeholder')}
        onClick={onOpen}
      >
        {selected && (
          <Flex
            _hover={{ bg: colors.hoverBackgroundColor }}
            align="center"
            bg={colors.backgroundColor}
            borderRadius="4"
            color="customgray.dark"
            flex="1"
            h="100%"
            minW="200px"
            px="2"
            textAlign="left"
          >
            {selected.text ?? <>&nbsp;</>}
          </Flex>
        )}
      </EntryModalFieldButton>
    </EntryModalField>
  );

  return (
    <ScheduleCategoryDataProvider>
      <CategorySelectContextProvider>
        {({ goBack }) => {
          return (
            <Popover
              isOpen={isOpen}
              trigger={trigger}
              onClose={() => {
                onClose();
                goBack();
              }}
            >
              <CategorySelect
                selected={selected}
                onClose={onClose}
                onSelect={onSelect}
              />
            </Popover>
          );
        }}
      </CategorySelectContextProvider>
    </ScheduleCategoryDataProvider>
  );
};
